import {
	DoneRounded,
	KeyboardArrowDownRounded,
	KeyboardArrowUpRounded,
	WarningAmberRounded,
} from "@mui/icons-material";
import { Collapse, IconButton, List, styled, Typography } from "@mui/material";
import { useField } from "formik";
import { useMemo, useState } from "react";
import { PercentageFuelRow } from "src/components/fleet/PercentageFuelRow";
import { PercentageRow } from "src/components/fleet/PercentageRow";
import type { FleetPercentageCategory } from "src/types";

interface Props {
	category: FleetPercentageCategory;
	categoryIndex: number;
	readOnly: boolean;
}

export function PercentageVehicleRow(props: Props) {
	const { category, categoryIndex, readOnly } = props;
	const [open, setOpen] = useState(false);

	const [field] = useField<FleetPercentageCategory>({
		name: `categories.${categoryIndex}`,
	});
	const total = useMemo(
		() =>
			field.value.fuels
				.map((fuel) => fuel.fuelPercentage)
				.reduce((a, b) => a + b, 0),
		[field.value.fuels],
	);
	const errorDifference = useMemo(() => 100 - total, [total]);
	const errorDisplay =
		errorDifference > 0
			? `-${errorDifference}%`
			: errorDifference < 0
				? `+${-errorDifference}%`
				: null;

	return (
		<>
			<Styles as="div">
				<div>
					<IconButton
						aria-label="expand row"
						size="small"
						onClick={() => setOpen(!open)}
					>
						{open ? (
							<KeyboardArrowUpRounded fontSize="small" />
						) : (
							<KeyboardArrowDownRounded fontSize="small" />
						)}
					</IconButton>
				</div>
				<Typography fontWeight={500} className="label">
					{category.categoryTranslation}
					<span
						className={`pill-container ${total !== 100 ? "pill-container--error" : ""}`.trim()}
					>
						{(open || errorDisplay) && (
							<>
								{errorDisplay ? (
									<WarningAmberRounded fontSize="small" />
								) : (
									<DoneRounded fontSize="small" />
								)}{" "}
								{total}%{errorDisplay && `(${errorDisplay})`}
							</>
						)}
					</span>
				</Typography>
				<Typography>{category.categoryPercentage} %</Typography>
			</Styles>
			<Collapse in={open} unmountOnExit>
				<List component="div" disablePadding>
					{category.fuels.map((fuel, fuelIndex) => (
						<PercentageFuelRow
							key={fuel.fuel}
							fuel={fuel}
							categoryIndex={categoryIndex}
							fuelIndex={fuelIndex}
							readOnly={readOnly}
						/>
					))}
				</List>
			</Collapse>
		</>
	);
}

const Styles = styled(PercentageRow)`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ theme }) => theme.palette.background.paper};
`;
